"use client";

import { useEffect } from "react";

const RELOAD_THROTTLE_MS = 15_000;
const RELOAD_TS_KEY = "runtime_asset_reload_ts";
const RELOAD_COUNT_KEY = "runtime_asset_reload_count";
const MAX_RELOADS_PER_TAB = 1;
const IS_ENABLED_BY_ENV = process.env.NEXT_PUBLIC_RUNTIME_ASSET_GUARD == "1";

function containsNextAssetPath(value: string): boolean {
  return value.indexOf("/_next/static/") != -1;
}

function shouldReloadForAssetError(
  message: string | undefined,
  sourceFile: string | undefined
): boolean {
  const msg = (message ?? "").toLowerCase();
  const file = sourceFile ?? "";
  const hasAssetContext = containsNextAssetPath(file) || containsNextAssetPath(message ?? "");

  if (containsNextAssetPath(file)) return true;

  if (msg.indexOf("chunkloaderror") != -1) return true;
  if (msg.indexOf("loading chunk") != -1 && hasAssetContext) return true;
  if (msg.indexOf("css chunk load failed") != -1) return true;
  // Keep this guard strict: these messages may come from API/proxy/extension issues
  // and should not trigger full-page reload unless they reference Next static assets.
  if (msg.indexOf("expected expression, got '<'") != -1 && hasAssetContext) return true;
  if (msg.indexOf("mime type") != -1 && msg.indexOf("text/html") != -1 && hasAssetContext) return true;
  if (msg.indexOf("failed to fetch dynamically imported module") != -1 && hasAssetContext) return true;
  if (msg.indexOf("dynamically imported module") != -1 && msg.indexOf("failed") != -1 && hasAssetContext) return true;

  return false;
}

function canPerformReload(): boolean {
  try {
    const now = Date.now();
    const prevRaw = localStorage.getItem(RELOAD_TS_KEY);
    const prev = prevRaw ? Number(prevRaw) : 0;
    const countRaw = localStorage.getItem(RELOAD_COUNT_KEY);
    let count = countRaw ? Number(countRaw) : 0;

    if (!Number.isFinite(prev) || now - prev >= RELOAD_THROTTLE_MS) {
      count = 0;
    }

    if (Number.isFinite(count) && count >= MAX_RELOADS_PER_TAB) {
      return false;
    }

    if (Number.isFinite(prev) && now - prev < RELOAD_THROTTLE_MS) {
      return false;
    }

    localStorage.setItem(RELOAD_TS_KEY, String(now));
    localStorage.setItem(
      RELOAD_COUNT_KEY,
      String(Number.isFinite(count) ? count + 1 : 1)
    );
    return true;
  } catch {
    // If localStorage is unavailable, allow one best-effort reload.
    return true;
  }
}

function tryReload(reason: string) {
  if (!canPerformReload()) return;
  console.warn("[runtime-asset-guard] Reloading page due to asset error:", reason);
  window.location.reload();
}

export default function RuntimeAssetGuard() {
  useEffect(() => {
    let enabled = IS_ENABLED_BY_ENV;

    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("assetGuard") == "on") enabled = true;
      if (params.get("assetGuard") == "off") {
        console.warn("[runtime-asset-guard] disabled by query param");
        return;
      }
    } catch {
      // no-op
    }

    if (!enabled) return;

    const onWindowError = (event: ErrorEvent) => {
      const message = event.message;
      const filename = event.filename;

      if (shouldReloadForAssetError(message, filename)) {
        tryReload(message ?? filename ?? "window.error");
        return;
      }

      const target = event.target as
        | HTMLScriptElement
        | HTMLLinkElement
        | null
        | undefined;

      if (target?.tagName == "SCRIPT") {
        const src = (target as HTMLScriptElement).src;
        if (src && containsNextAssetPath(src)) {
          tryReload(src);
        }
      }

      if (target?.tagName == "LINK") {
        const href = (target as HTMLLinkElement).href;
        if (href && containsNextAssetPath(href)) {
          tryReload(href);
        }
      }
    };

    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      const reasonText =
        typeof reason == "string"
          ? reason
          : (reason?.message as string | undefined) ?? String(reason ?? "");

      if (shouldReloadForAssetError(reasonText, undefined)) {
        tryReload(reasonText);
      }
    };

    window.addEventListener("error", onWindowError, true);
    window.addEventListener("unhandledrejection", onUnhandledRejection);

    return () => {
      window.removeEventListener("error", onWindowError, true);
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
    };
  }, []);

  return null;
}
